import { API_BASE_URL } from "../config";
import React, { useState, useEffect } from "react";
import axios from "axios";

const MarginManagement = () => {
  const [overrides, setOverrides] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [_config, setConfig] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Default config edit state
  const [equityDelivery, setEquityDelivery] = useState(100);
  const [equityIntraday, setEquityIntraday] = useState(20);
  const [futures, setFutures] = useState(15);
  const [optionsBuy, setOptionsBuy] = useState(100);
  const [optionsSell, setOptionsSell] = useState(20);
  const [configMessage, setConfigMessage] = useState("");

  // New Override State
  const [selectedUser, setSelectedUser] = useState("");
  const [category, setCategory] = useState("equityIntraday");
  const [customMargin, setCustomMargin] = useState(10);
  const [overrideMessage, setOverrideMessage] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const configRes = await axios.get(`${API_BASE_URL}/api/margin/config`);
      const configData = configRes.data || {};
      setConfig(configData);
      setEquityDelivery(configData.equityDelivery || 100);
      setEquityIntraday(configData.equityIntraday || 20);
      setFutures(configData.futures || 15);
      setOptionsBuy(configData.optionsBuy || 100);
      setOptionsSell(configData.optionsSell || 20);

      const overrideRes = await axios.get(`${API_BASE_URL}/api/margin/overrides`);
      setOverrides(overrideRes.data || []);

      const userRes = await axios.get(`${API_BASE_URL}/api/admin/users`);
      setUsers(userRes.data || []);
    } catch (err) {
      console.error("Error fetching margin data", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    setConfigMessage("");
    try {
      await axios.post(`${API_BASE_URL}/api/margin/config`, {
        equityDelivery: Number(equityDelivery),
        equityIntraday: Number(equityIntraday),
        futures: Number(futures),
        optionsBuy: Number(optionsBuy),
        optionsSell: Number(optionsSell),
      });
      setConfigMessage("Platform default category margins updated successfully!");
      fetchData();
    } catch (err) {
      alert("Failed to update default margins");
    }
  };

  const handleSetOverride = async (e) => {
    e.preventDefault();
    setOverrideMessage("");
    if (!selectedUser) return alert("Select a client user");
    try {
      const res = await axios.post(`${API_BASE_URL}/api/margin/overrides`, {
        userId: selectedUser,
        instrumentCategory: category,
        marginPercent: Number(customMargin),
      });
      setOverrideMessage(res.data.message);
      fetchData();
    } catch (err) {
      alert("Failed to set margin override");
    }
  };

  const handleRemoveOverride = async (id) => {
    if (!window.confirm("Revert this client's custom margin back to platform default?")) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/margin/overrides/${id}`);
      fetchData();
    } catch (err) {
      alert("Failed to remove override");
    }
  };

  return (
    <div>
      <div style={{ marginBottom: "20px" }}>
        <h2 style={{ margin: 0, color: "#fff" }}>Margin Management & Risk Control</h2>
        <p style={{ margin: "4px 0 0", color: "#b2b5be", fontSize: "14px" }}>
          Configure platform default margin percentages per category and assign custom per-client absolute margin overrides
        </p>
      </div>

      <div style={{ backgroundColor: "rgba(255, 152, 0, 0.15)", color: "#ffb74d", padding: "12px 16px", borderRadius: "6px", marginBottom: "24px", borderLeft: "4px solid #ff9800", fontSize: "13px" }}>
        ⚠️ <strong>INDICATIVE MARGIN ENGINE</strong>: These margin numbers govern paper-trading margin requirements. They are flat percentage requirements and not exact broker SPAN margins.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "30px" }}>
        {/* Platform Default Margins Form */}
        <div style={{ backgroundColor: "#1e222d", borderRadius: "8px", padding: "20px", border: "1px solid #2a2e39" }}>
          <h3 style={{ marginTop: 0, color: "#fff", fontSize: "16px" }}>1. Platform Default Category Margins (%)</h3>

          {configMessage && <div style={{ backgroundColor: "rgba(76, 175, 80, 0.2)", color: "#81c784", padding: "8px 12px", borderRadius: "4px", marginBottom: "12px", fontSize: "13px" }}>{configMessage}</div>}

          <form onSubmit={handleUpdateConfig}>
            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Equity Delivery (CNC) Margin %</label>
              <input type="number" value={equityDelivery} onChange={(e) => setEquityDelivery(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Equity Intraday (MIS) Margin %</label>
              <input type="number" value={equityIntraday} onChange={(e) => setEquityIntraday(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Futures (Index & Stock) Margin %</label>
              <input type="number" value={futures} onChange={(e) => setFutures(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Options Buy Premium %</label>
              <input type="number" value={optionsBuy} onChange={(e) => setOptionsBuy(e.target.value)} style={inputStyle} />
            </div>
            <div style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>Options Sell / Write Notional Margin %</label>
              <input type="number" value={optionsSell} onChange={(e) => setOptionsSell(e.target.value)} style={inputStyle} />
            </div>

            <button type="submit" style={btnStyle}>Save Platform Defaults</button>
          </form>
        </div>

        {/* Set Custom Override Form */}
        <div style={{ backgroundColor: "#1e222d", borderRadius: "8px", padding: "20px", border: "1px solid #2a2e39" }}>
          <h3 style={{ marginTop: 0, color: "#fff", fontSize: "16px" }}>2. Set Custom Absolute Override Per Client</h3>

          {overrideMessage && <div style={{ backgroundColor: "rgba(76, 175, 80, 0.2)", color: "#81c784", padding: "8px 12px", borderRadius: "4px", marginBottom: "12px", fontSize: "13px" }}>{overrideMessage}</div>}

          <form onSubmit={handleSetOverride}>
            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Select Client User</label>
              <select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)} style={inputStyle}>
                <option value="">-- Choose Client --</option>
                {users.map((u) => (
                  <option key={u._id} value={u._id}>{u.name} ({u.email})</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: "12px" }}>
              <label style={labelStyle}>Instrument Category</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
                <option value="equityDelivery">Equity Delivery (CNC)</option>
                <option value="equityIntraday">Equity Intraday (MIS)</option>
                <option value="futures">Futures</option>
                <option value="optionsBuy">Options Buy</option>
                <option value="optionsSell">Options Sell</option>
              </select>
            </div>

            <div style={{ marginBottom: "16px" }}>
              <label style={labelStyle}>Custom Margin Percentage (%)</label>
              <input type="number" min="0.1" max="100" step="0.1" value={customMargin} onChange={(e) => setCustomMargin(e.target.value)} style={inputStyle} />
            </div>

            <button type="submit" style={btnPrimaryStyle}>Apply Client Override</button>
          </form>
        </div>
      </div>

      {/* Active Overrides Table */}
      <div style={{ backgroundColor: "#1e222d", borderRadius: "8px", overflow: "hidden", border: "1px solid #2a2e39" }}>
        <div style={{ padding: "16px", borderBottom: "1px solid #2a2e39" }}>
          <h3 style={{ margin: 0, color: "#fff", fontSize: "16px" }}>Active Per-Client Custom Overrides ({overrides.length})</h3>
        </div>

        {loading ? (
          <div style={{ padding: "20px", color: "#888" }}>Loading overrides...</div>
        ) : overrides.length === 0 ? (
          <div style={{ padding: "24px", color: "#888", textAlign: "center" }}>No custom client margin overrides active. All clients use platform defaults.</div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ backgroundColor: "#2a2e39", color: "#b2b5be", textAlign: "left" }}>
                <th style={thStyle}>Client Name</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Category</th>
                <th style={thStyle}>Custom Margin %</th>
                <th style={thStyle}>Set By Admin</th>
                <th style={thStyle}>Last Updated</th>
                <th style={thStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {overrides.map((o) => (
                <tr key={o._id} style={{ borderBottom: "1px solid #2a2e39" }}>
                  <td style={{ ...tdStyle, fontWeight: "bold", color: "#fff" }}>{o.userId?.name || "N/A"}</td>
                  <td style={tdStyle}>{o.userId?.email || "N/A"}</td>
                  <td style={{ ...tdStyle, color: "#ffb74d", fontWeight: "bold" }}>{o.instrumentCategory}</td>
                  <td style={{ ...tdStyle, fontWeight: "bold", color: "#4caf50" }}>{o.marginPercent}%</td>
                  <td style={tdStyle}>{o.setBy?.name || "Admin"}</td>
                  <td style={tdStyle}>{new Date(o.updatedAt).toLocaleDateString()}</td>
                  <td style={tdStyle}>
                    <button onClick={() => handleRemoveOverride(o._id)} style={revertBtnStyle}>
                      ↩ Revert to Default
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const thStyle = { padding: "12px", fontSize: "12px", textTransform: "uppercase" };
const tdStyle = { padding: "12px", color: "#d1d4dc" };

const labelStyle = {
  display: "block",
  fontSize: "13px",
  fontWeight: 500,
  marginBottom: "6px",
  color: "#d1d4dc",
};

const inputStyle = {
  width: "100%",
  padding: "10px",
  borderRadius: "6px",
  border: "1px solid #434651",
  backgroundColor: "#131722",
  color: "#fff",
  fontSize: "14px",
  boxSizing: "border-box",
};

const btnStyle = {
  padding: "10px 18px",
  backgroundColor: "#2962ff",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontWeight: "bold",
  cursor: "pointer",
};

const btnPrimaryStyle = {
  padding: "10px 18px",
  backgroundColor: "#4caf50",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontWeight: "bold",
  cursor: "pointer",
};

const revertBtnStyle = {
  padding: "4px 8px",
  backgroundColor: "rgba(239, 83, 80, 0.15)",
  color: "#ef5350",
  border: "1px solid #ef5350",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "11px",
};

export default MarginManagement;
